import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'
import type { SingleValue, OnChangeValue } from 'react-select'

import { IKReactSelect } from '../../../../ui-kit'
import type { InputFormProps as Props } from '../../../interfaces'
import { getFieldsMapping } from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'

const FormatInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: string | string[]) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em">
        <VStack alignItems="stretch" width="full" spacing="0.5em">
          <OperatorMenu
            width="full"
            expression={expression}
            handleOperatorChange={handleOperatorChange}
          />

          {['IS', 'IS_NOT'].includes(expression.operator) &&
            !Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactSelect
                  menuPlacement='auto'
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Select a format"
                  isSearchable={true}
                  options={getFieldsMapping({
                    fieldName: expression.fieldName,
                  }).possibleValues?.map(val => ({
                    label: val as string,
                    value: val as string,
                  }))}
                  value={
                    expression.value
                      ? {
                          label: expression.value as string,
                          value: expression.value as string,
                        }
                      : null
                  }
                  onChange={(
                    newVal: SingleValue<{
                      label: string
                      value: string
                    }>,
                  ) => {
                    newVal?.value && handleValueChange(newVal.value)
                    setDirty(true)
                  }}
                />
              </Box>
            )}
          {['IS_ONE_OF', 'IS_NONE_OF'].includes(expression.operator) &&
            Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactSelect
                  menuPlacement='auto'
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Select formats"
                  isSearchable={true}
                  isMulti
                  options={getFieldsMapping({
                    fieldName: expression.fieldName,
                  }).possibleValues?.map(val => ({
                    label: val as string,
                    value: val as string,
                  }))}
                  value={
                    expression.value.map(val => ({
                      value: val as string,
                      label: val as string,
                    })) ?? []
                  }
                  onChange={(
                    arr: OnChangeValue<
                      {
                        label: string
                        value: string
                      },
                      true
                    >,
                  ) => {
                    const newVal = arr.map(val => val.value)
                    handleValueChange(newVal)
                    setDirty(true)
                  }}
                />
              </Box>
            )}
        </VStack>

        <ChakraFormErrorMessage width="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default FormatInputForm
